import { IAMService, User, UserIdentifier } from '@vendasta/iamv2';
import {
  CrmDependencies,
  CrmObjectDependencies,
  ObjectType,
  SimplifiedUser,
  UserWithDisplayName,
} from './tokens-and-interfaces';
import {
  GalaxyDateDefault,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
  GalaxyFilterType,
  GalaxyFilterValueInterface,
} from '@vendasta/galaxy/filter/chips';
import {
  FilterGroupInterface,
  FilterValueInterface,
  DateDefault,
  FilterGroupOperator,
  FilterInterface,
  FilterOperator,
  FilterType,
} from '@vendasta/crm';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PAGE_ROUTES } from './constants';

export function userFilterFn(user: UserWithDisplayName, searchTerm: string): boolean {
  searchTerm = searchTerm?.toLowerCase();
  const includesFullName = user?.displayName?.toLowerCase()?.includes(searchTerm);
  const includesFirstName = user?.firstName?.toLowerCase()?.includes(searchTerm);
  const includesLastName = user?.lastName?.toLowerCase()?.includes(searchTerm);
  const includesUserId = user?.userId?.toLowerCase()?.includes(searchTerm);
  const includesEmail = user?.email?.toLowerCase()?.includes(searchTerm);
  return includesFullName || includesFirstName || includesLastName || includesUserId || includesEmail;
}

export function convertToReadableUsers(users: User[] | SimplifiedUser[]): UserWithDisplayName[] {
  return users.map(
    (user) =>
      ({
        ...user,
        displayName: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || (user as SimplifiedUser).displayName || user?.userId,
      }) as UserWithDisplayName,
  );
}

export function convertGalaxyFilters(filters: GalaxyFilterInterface[] | undefined): FilterGroupInterface | undefined {
  if (!filters) {
    return undefined;
  }
  const filterGroup = {} as FilterGroupInterface;
  filterGroup.operator = FilterGroupOperator.FILTER_GROUP_OPERATOR_AND;
  filterGroup.filters = [];
  filters.forEach((inputFilter) => {
    if (!inputFilter.fieldId || !inputFilter.operator) {
      return;
    }
    const filterOperator: FilterOperator | undefined = inputFilter.operator
      ? FilterOperator[GalaxyFilterOperator[inputFilter.operator] as keyof typeof FilterOperator]
      : undefined;
    const filterGroupFilter: FilterInterface = {
      fieldId: inputFilter.fieldId,
      operator: filterOperator,
      values: [],
      floatTolerance: inputFilter.floatTolerance,
    };
    filterGroupFilter.values = inputFilter.values ? convertFromGalaxyFilterValues(inputFilter.values) : undefined;
    filterGroup.filters?.push(filterGroupFilter);
  });
  return filterGroup;
}

export function convertToGalaxyFilter(filter: FilterInterface): GalaxyFilterInterface {
  return {
    fieldId: filter.fieldId,
    operator: filter.operator
      ? convertToGalaxyFilterOperator(filter.operator)
      : GalaxyFilterOperator.FILTER_OPERATOR_INVALID,
    values: filter.values ? convertToGalaxyFilterValues(filter.values) : [],
    floatTolerance: filter.floatTolerance,
  } as GalaxyFilterInterface;
}

export function convertToGalaxyFilterType(type: FilterType): GalaxyFilterType {
  return GalaxyFilterType[FilterType[type] as keyof typeof GalaxyFilterType];
}

export function convertToGalaxyFilterOperator(operator: FilterOperator): GalaxyFilterOperator {
  return GalaxyFilterOperator[FilterOperator[operator] as keyof typeof GalaxyFilterOperator];
}

export function convertToGalaxyFilterOperators(operators: FilterOperator[]): GalaxyFilterOperator[] {
  return operators.map((o) => convertToGalaxyFilterOperator(o));
}

export function convertToGalaxyFilterValues(values: FilterValueInterface[]): GalaxyFilterValueInterface[] {
  return values.map((v) => convertToGalaxyFilterValue(v));
}

export function convertToGalaxyFilterValue(value: FilterValueInterface): GalaxyFilterValueInterface {
  const dateDefault: GalaxyDateDefault | undefined = value.dateDefault
    ? GalaxyDateDefault[DateDefault[value.dateDefault] as keyof typeof GalaxyDateDefault]
    : undefined;
  return {
    boolean: value.boolean,
    integer: value.integer,
    string: value.string,
    date: value.date,
    dateDefault: dateDefault,
    float: value.float,
    phone: value.phone,
  } as GalaxyFilterValueInterface;
}

export function convertFromGalaxyFilterValues(values: GalaxyFilterValueInterface[]): FilterValueInterface[] {
  return values.map((v) => convertFromGalaxyFilterValue(v));
}

export function convertFromGalaxyFilterValue(value: GalaxyFilterValueInterface): FilterValueInterface {
  const dateDefault: DateDefault | undefined = value.dateDefault
    ? DateDefault[GalaxyDateDefault[value.dateDefault] as keyof typeof DateDefault]
    : undefined;
  return {
    boolean: value.boolean,
    integer: value.integer,
    string: value.string,
    date: value.date,
    dateDefault: dateDefault,
    float: value.float,
    phone: value.phone,
  } as FilterValueInterface;
}

export function getObjectDependencies(objectType: ObjectType, parentConfig: CrmDependencies): CrmObjectDependencies {
  const emptyConfig: CrmObjectDependencies = {};
  switch (objectType) {
    case 'Company':
      return parentConfig.company || emptyConfig;
    case 'Contact':
      return parentConfig.contact || emptyConfig;
    case 'Activity':
      return parentConfig.activity || emptyConfig;
    case 'Opportunity':
      return parentConfig.opportunity || emptyConfig;
    default:
      return emptyConfig;
  }
}

export function getMultiUsers(
  uniqueUserIds: string[],
  config: CrmDependencies,
  iamService: IAMService,
): Observable<SimplifiedUser[] | User[]> {
  if (config.services?.userService?.getMultiUsers) {
    return config.services.userService.getMultiUsers(uniqueUserIds).pipe(
      switchMap((users) => {
        const resolvedUsers = users.filter((u) => !!u.userId) as SimplifiedUser[];
        const resolvedIds = new Set(resolvedUsers.map((u) => u.userId));
        const missingIds = uniqueUserIds.filter((id) => !resolvedIds.has(id));
        if (!missingIds.length || !iamService) {
          return of(resolvedUsers);
        }
        const userIdentifiers = missingIds.map((userId) => new UserIdentifier({ userId }));
        return iamService.getMultiUsers(userIdentifiers).pipe(
          map((iamUsers) => [...resolvedUsers, ...iamUsers] as SimplifiedUser[]),
          catchError(() => of(resolvedUsers)),
        );
      }),
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
    );
  } else if (iamService) {
    const uniqueUserIdentifiers = uniqueUserIds.map((userId) => new UserIdentifier({ userId: userId }));
    return iamService.getMultiUsers(uniqueUserIdentifiers).pipe(
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
    );
  } else {
    throw new Error('Service not defined (CRMUserService | IAMService)');
  }
}

/**
 * Builds a CRM entity profile URL for the given object type and ID.
 * Handles multi-location routing and supports Contact, Company, and Opportunity types.
 *
 * @param namespace - The CRM namespace observable
 * @param routePrefix - The route prefix observable (e.g., '/crm')
 * @param objectType - The CRM object type (Contact, Company, Opportunity)
 * @param objectId - The CRM object ID
 * @param isMultiLocation - Optional observable indicating if multi-location is enabled
 * @param multiLocationRoutePrefix - Optional route prefix observable for multi-location contexts
 * @returns Observable that emits the constructed URL or empty string if not applicable
 *
 * @example
 * buildCrmEntityUrl(
 *   config.namespace$,
 *   config.routePrefix$,
 *   'Contact',
 *   'ContactID-123',
 *   multiLocation.isMultiLocation$,
 *   multiLocation.routePrefix$
 * ).subscribe(url => console.log(url));
 * // Outputs: '/crm/contact/profile/ContactID-123'
 */
export function buildCrmEntityUrl(
  namespace: Observable<string | undefined>,
  routePrefix: Observable<string | undefined>,
  objectType: ObjectType,
  objectId: string,
  isMultiLocation?: Observable<boolean>,
  multiLocationRoutePrefix?: Observable<string | undefined>,
): Observable<string> {
  const isMultiLocation$ = isMultiLocation ?? of(false);
  const actualRoutePrefix$ = isMultiLocation$.pipe(
    switchMap((isMultiLoc) => {
      return isMultiLoc && multiLocationRoutePrefix ? multiLocationRoutePrefix : routePrefix;
    }),
  );

  return combineLatest([actualRoutePrefix$, isMultiLocation$, namespace]).pipe(
    map(([pathRoot, isMultiLoc, ns]) => {
      if (!ns || !pathRoot) {
        return '';
      }

      let pathTemplate = '';

      switch (objectType) {
        case 'Company':
          if (isMultiLoc) {
            return '';
          }
          pathTemplate = `${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.PROFILE}`;
          break;
        case 'Contact':
          if (isMultiLoc) {
            pathTemplate = `${PAGE_ROUTES.MULTI_LOCATION.CONTACTS.ROOT}/${PAGE_ROUTES.MULTI_LOCATION.CONTACTS.SUBROUTES.PROFILE}`;
          } else {
            pathTemplate = `${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.PROFILE}`;
          }
          break;
        case 'Opportunity':
          pathTemplate = `${PAGE_ROUTES.OPPORTUNITY.ROOT}/${PAGE_ROUTES.OPPORTUNITY.SUBROUTES.PROFILE}`;
          break;
        default:
          return '';
      }

      if (!pathTemplate) {
        return '';
      }

      return `${pathRoot}/${pathTemplate}`.replace(':crmObjectId', objectId).replace(':namespace', ns);
    }),
    catchError(() => of('')),
  );
}
